import { alpha, type PaletteMode } from '@mui/material/styles';
import { brand, brandDark } from './tokens';

declare module '@mui/material/styles' {
  interface TypeBackground {
    /** L2 surface: dialogs, menus, popovers, snackbars. Equals `paper` in light. */
    raised: string;
    /** L3 surface: tooltips, filled default chips, avatar fallbacks. */
    top: string;
  }
}

// Dark values come from `brandDark`; the light branch keeps its original literals so it can be diffed against dev
// and seen not to change.
export function getPaletteOptions(mode: PaletteMode, isDark: boolean, bodyColor: string, secondaryText: string) {
  return {
    mode,
    // Override grey scale for dark mode so grey.50/100/200 aren't near-white
    ...(isDark && {
      grey: {
        50: '#1b2620',
        100: '#212e26',
        200: '#293a30',
        300: '#33473b',
        400: '#4a5a50',
        500: '#6c7a70',
        600: '#8c9a90',
        700: '#aab6ac',
        800: '#cdd5cc',
        900: '#e8ede6',
        A100: '#293a30',
        A200: '#33473b',
        A400: '#5b6b60',
        A700: '#8c9a90',
      },
    }),
    primary: isDark
      ? { main: brandDark.accent, light: brandDark.accentLight, dark: brandDark.accentDeep, contrastText: brandDark.onAccent }
      : { main: brand.accent, light: '#34b377', dark: brand.accentDeep, contrastText: '#FFFFFF' },
    secondary: {
      main: isDark ? brand.paper : brand.ink,
      light: isDark ? brandDark.raised : brand.inkSoft,
      dark: '#0e1813',
      contrastText: isDark ? brand.ink : brand.paper,
    },
    error: isDark
      ? { main: brandDark.error, light: brandDark.errorLight, dark: brandDark.errorDeep, contrastText: brandDark.onAccent }
      : { main: brand.error, light: '#D9594E', dark: '#9e2d22', contrastText: '#FFFFFF' },
    warning: isDark
      ? {
          main: brandDark.warning,
          light: brandDark.warningLight,
          dark: brandDark.warningDeep,
          contrastText: brandDark.onAccent,
        }
      : { main: brand.warning, light: '#E0A23E', dark: '#9c6313', contrastText: '#FFFFFF' },
    // Light keeps MUI's default info palette.
    ...(isDark && {
      info: { main: brandDark.info, light: brandDark.infoLight, dark: brandDark.infoDeep, contrastText: brandDark.onAccent },
    }),
    success: isDark
      ? { main: brandDark.accent, light: brandDark.accentLight, dark: brandDark.accentDeep, contrastText: brandDark.onAccent }
      : { main: brand.accent, light: brand.successDot, dark: brand.accentDeep, contrastText: '#FFFFFF' },
    background: isDark
      ? { default: brand.ink, paper: brand.inkSoft, raised: brandDark.raised, top: brandDark.top }
      : { default: brand.paper, paper: brand.card, raised: brand.card, top: brand.paperAlt },
    text: {
      primary: bodyColor,
      secondary: secondaryText,
      // 0.6 rather than 0.5: several screens use text.disabled for empty-state copy, which must still read on L1/L2.
      ...(isDark && { disabled: 'rgba(238,242,232,0.6)' }),
    },
    divider: isDark ? brandDark.line : brand.line,
    action: isDark
      ? {
          hover: alpha(brand.paper, 0.08),
          selected: alpha(brandDark.accent, 0.2),
          focus: alpha(brandDark.accent, 0.24),
          disabled: 'rgba(238,242,232,0.3)',
          disabledBackground: 'rgba(238,242,232,0.12)',
        }
      : {
          hover: alpha(brand.ink, 0.06),
          selected: alpha(brand.accent, 0.14),
          // MUI's own alphas on the ink. Never undefined: createTheme copies an explicit undefined over MUI's
          // default, and every disabled control kept its enabled colours (card a0c931a4).
          disabled: alpha(brand.ink, 0.26),
          disabledBackground: alpha(brand.ink, 0.12),
        },
  };
}
